// ./src/utils/api/AppAddonService.ts
//
// Buying a top-up bundle: what is for sale, and starting the purchase.
//
// ⚠ THE CLIENT NEVER GRANTS CREDITS, and that is the whole security model of this rail. The
// server returns a `clientSecret` and nothing else; the credits are granted once STRIPE reports
// the money taken — ms_auth's `payment_intent.succeeded` webhook, or `confirmPayment` below, which
// sends only the intent's ID: ms_auth reads that intent from Stripe itself and grants from what
// Stripe says (2026-10-28 — a brand with no webhook endpoint charged every upgrade and granted
// none). So the sequence is: create intent → pay IN PLACE → confirm → POLL for the grant.
//
// ⚠ IN PLACE, NEVER A REDIRECT — and this is a correctness requirement, not a preference.
// Top-ups are bought from inside the message composer, mid-send, with recipients chosen and a
// message written. A hosted-checkout redirect navigates the app away and DESTROYS that work.
// The first version of this did exactly that and had to be rebuilt; the PaymentElement flow
// (the same one guests use to buy tickets) keeps the page, and therefore the send, intact.
//
// ⚠ Prices come from Stripe, not from a column, so what is shown is what will be charged.
// A bundle whose price cannot be read comes back `purchasable: false` — render it disabled
// rather than hiding it, or a host wonders where the bundle went.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const ADDON_CATALOG_QUERY = gql`
  query AppAddonCatalog($params: AppAddonCatalogInput) {
    appAddonCatalog(params: $params) {
      code
      data {
        providerSku
        name
        featureCode
        addonType
        credits
        maxAddons
        purchasedCount
        limitReached
        amount
        currency
        purchasable
      }
      errors { name errors warnings }
    }
  }
`;

const ADDON_CONFIRM_MUTATION = gql`
  mutation AppAddonPaymentConfirm($params: AppAddonPaymentConfirmInput) {
    appAddonPaymentConfirm(params: $params) {
      code
      data {
        paymentIntentId
        status
        granted
        alreadyGranted
      }
      errors { name errors warnings }
    }
  }
`;

const ADDON_INTENT_MUTATION = gql`
  mutation AppAddonPaymentIntentCreate($params: AppAddonPaymentIntentInput) {
    appAddonPaymentIntentCreate(params: $params) {
      code
      data {
        clientSecret
        publishableKey
        paymentIntentId
        amount
        currency
        amountBeforeDiscount
        discountAmount
        discountCode
        granted
        creditsGranted
      }
      errors { name errors warnings }
    }
  }
`;

export interface AppAddonProduct {
  providerSku?: string | null;
  name?: string | null;
  featureCode?: string | null;
  /**
   * `MULTIPLE` = a credit bundle. `SERVICE` = something a human delivers.
   *
   * ⚠ The ONLY thing that tells the two apart. They are bought, granted, refunded and
   * discounted by identical code, so nothing else in the payload distinguishes them.
   */
  addonType?: string | null;
  credits?: number | null;
  /** How many times this may be bought per account+scope. Null = unlimited. */
  maxAddons?: number | null;
  purchasedCount?: number | null;
  /**
   * ⚠ Already owned, not broken. Kept apart from `purchasable` because "you have this" and
   * "this has no price" need different words on screen.
   */
  limitReached?: boolean | null;
  /** A decimal, already divided out of Stripe's minor units. Null when unreadable. */
  amount?: number | null;
  currency?: string | null;
  purchasable?: boolean | null;
}

export interface AppAddonPaymentIntent {
  clientSecret?: string | null;
  /** ⚠ The PLATFORM key. No connected account — top-up money is the platform's, not the host's. */
  publishableKey?: string | null;
  paymentIntentId?: string | null;
  /** What will actually be charged — the DISCOUNTED figure when a code applied. */
  amount?: number | null;
  currency?: string | null;
  /** The list price. Equals `amount` when no code was used. */
  amountBeforeDiscount?: number | null;
  discountAmount?: number | null;
  discountCode?: string | null;
  /**
   * ⚠ TRUE means the purchase is ALREADY COMPLETE and nothing is owed — a code covered the whole
   * bundle, so the server granted the credits directly and there is no payment to make.
   * `clientSecret` is null and PaymentElement must NOT be mounted.
   *
   * ⚠ Read this flag; never infer it from `amount === 0`. An amount too small to charge is a
   * REFUSAL on the server, not a free grant, so the two must not be conflated here either.
   */
  granted?: boolean | null;
  creditsGranted?: number | null;
}

export class AppAddonService {
  /**
   * What this space sells.
   *
   * ⚠ SERVICES ARE EXCLUDED UNLESS ASKED FOR. With no `addonType` the server returns only
   * NON-service products, so the top-up drawer can never hand someone a concierge block among
   * the message bundles. Pass `addonType: "SERVICE"` to list the services instead.
   *
   * ⚠ `network-only`: a purchase changes `purchasedCount`/`limitReached`, and a cached catalog
   * would keep offering a once-only service someone has just bought.
   *
   * ⚠ NO SCOPE ARGUMENT, and `npm run check-usage` enforces that. The server's input DOES accept
   * one — a purchase limit counts per scope, so a future per-event service will need it — but
   * the CLIENT does not choose scopes on this rail (A2.9.2), and an unused optional scope on a
   * purchase path is precisely what that ruling removed. Adding it back is a deliberate act for
   * per-event services, with its own reasoning, not a convenience.
   */
  static async catalog(params?: { featureCode?: string | null; addonType?: string | null }): Promise<OpResult> {
    return ApiService.executeQuery(
      ADDON_CATALOG_QUERY,
      {
        params: {
          featureCode: params?.featureCode || undefined,
          addonType: params?.addonType || undefined,
        },
      },
      { fetchPolicy: "network-only" }
    );
  }

  /**
   * Starts a purchase. Returns a `clientSecret` for PaymentElement — pay without navigating.
   *
   * ⚠ NO SCOPE ARGUMENT, DELIBERATELY (owner's ruling, ROADMAP A2.9.2). Every top-up bought
   * from this app lands on the ACCOUNT and is spendable on any event. This method used to take
   * a `scopeRef` so the event dashboard could buy `event:<id>` credits; that is reversed, and
   * the parameter is GONE rather than left unused — an optional scope on a call that takes
   * money is something a future caller will pass without knowing the ruling, and the failure
   * is silent (money in the right amount on the wrong wallet, no error anywhere).
   *
   * ⚠ THE SERVER STILL ACCEPTS `scopeRef` AND MUST KEEP DOING SO. `AppAddonPaymentIntentInput`
   * is unchanged; omitting the variable simply defaults it to the account scope. ms_auth's
   * spend order is event credits → account credits → allowance, and A9.4's per-event upgrades
   * are the obvious future writer of the event tier. Re-adding this argument is a deliberate
   * act for that feature — with its own reasoning — not a tidy-up.
   *
   * ⚠ And note what has NOT been re-litigated: ms_auth never validated `scopeRef` (it has no
   * concept of an event), which was an accepted risk while the client chose the scope. Sending
   * no scope makes that moot for this rail; it is not a gap that needs new validation.
   *
   * @param discountCode an optional promotion code the customer typed.
   *
   * ⚠ THE CLIENT SENDS A CODE AND NEVER AN AMOUNT. Validation, pricing and the redemption
   * record all happen server-side — Stripe does not apply promotion codes to a raw
   * PaymentIntent, so there is nothing here that could be trusted to compute a price. A bad
   * code comes back as a failed OpResult carrying a reason worth showing verbatim.
   *
   * ⚠ A code that covers the WHOLE bundle returns `granted: true` and no `clientSecret`: the
   * credits already exist and there is nothing to pay. See the type.
   */
  static async createPaymentIntent(providerSku: string, discountCode?: string | null): Promise<OpResult> {
    const code = String(discountCode ?? "").trim();

    return ApiService.executeMutation(ADDON_INTENT_MUTATION, {
      params: { providerSku, ...(code ? { discountCode: code } : {}) },
    });
  }

  /**
   * Stripe took the card: ask ms_auth to record the purchase now, rather than wait for its webhook. Only the ID is sent —
   * ms_auth reads the intent from Stripe and grants only a succeeded one of this account's. Idempotent with the webhook.
   * ⓘ The caller still POLLS for the grant: a failed confirm (offline, a payment still settling) is not a failed purchase.
   */
  static async confirmPayment(paymentIntentId: string): Promise<OpResult> {
    return ApiService.executeMutation(ADDON_CONFIRM_MUTATION, { params: { paymentIntentId } });
  }
}
